import { ActionTree } from 'vuex';
import { RootState } from '@/plugins/store';
import { EquipementAction } from './actions-types';
import EquipementStatsService from '../services/EquipementStatsService';
import ErrorManager from '@/common/utils/ErrorManager';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import { EQUIPEMENT } from '@/modules/Equipements/store/mutation-types';
import { EquipementState } from '@/modules/Equipements/store/state';
import EquipementService from '@/modules/Equipements/services/EquipementService';
import { NavFilter } from '@/models/NavFilter';
import { PreventiveData } from '@/models/PreventiveData';

export const actions: ActionTree<EquipementState, RootState> = {
    async [EquipementAction.GET_NON_PREVENTIVE_STATS]({ commit, state }, id: number): Promise<void> {
        if (
            state.nonPreventiveStats &&
            state.detailsEquipement.phaseId === id &&
            state.nonPreventiveStats.nbEnCours !== 0
        ) {
            return;
        }
        commit(EQUIPEMENT.SET_NON_PREVENTIVE_STATS_LOADING, true);
        commit(EQUIPEMENT.SET_ERROR, false);
        try {
            const response = await EquipementStatsService.getNonPreventiveInterventionStatsByPhase(id);
            commit(EQUIPEMENT.SET_NON_PREVENTIVE_STATS, response.data);
        } catch (error) {
            commit(EQUIPEMENT.SET_ERROR, true);
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        } finally {
            commit(EQUIPEMENT.SET_NON_PREVENTIVE_STATS_LOADING, false);
        }
    },

    async [EquipementAction.GET_PREVENTIVE_STATS_CIRCLE]({ commit, state }, id: number): Promise<void> {
        if (
            state.preventiveStatsCircle &&
            state.detailsEquipement.phaseId === id &&
            state.preventiveStatsCircle.inProgressTotal !== 0
        ) {
            return;
        }
        commit(EQUIPEMENT.SET_PREVENTIVE_STATS_CIRCLE_LOADING, true);
        commit(EQUIPEMENT.SET_ERROR, false);
        try {
            const response = await EquipementStatsService.getPreventiveInterventionStatsByPhasePeriod(id);
            const preventiveData: PreventiveData = response.data;
            commit(EQUIPEMENT.SET_PREVENTIVE_STATS_CIRCLE, {
                inProgressTotal: preventiveData.nbEnCours,
                achievedTotal: preventiveData.nbRealisees,
                awaitingRealizationTotal: preventiveData.nbARealiser,
            });
        } catch (error) {
            commit(EQUIPEMENT.SET_ERROR, true);
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        } finally {
            commit(EQUIPEMENT.SET_PREVENTIVE_STATS_CIRCLE_LOADING, false);
        }
    },

    async [EquipementAction.GET_PREVENTIVE_STATS_CHART]({ commit, state }, id: number): Promise<void> {
        if (
            state.preventiveStatsChart &&
            state.detailsEquipement.phaseId === id &&
            state.preventiveStatsChart.inProgress.length > 0
        ) {
            return;
        }
        commit(EQUIPEMENT.SET_PREVENTIVE_STATS_CHART_LOADING, true);
        commit(EQUIPEMENT.SET_ERROR, false);
        try {
            const response = await EquipementStatsService.getPreventiveInterventionStatsByMonth(id);
            const preventiveData: PreventiveData[] = response.data;
            const enCours: number[] = [];
            const realisees: number[] = [];
            const aRealiser: number[] = [];
            preventiveData.forEach((element: PreventiveData) => {
                enCours.push(element.nbEnCours);
                realisees.push(element.nbRealisees);
                aRealiser.push(element.nbARealiser);
            });
            commit(EQUIPEMENT.SET_PREVENTIVE_STATS_CHART, {
                inProgress: enCours,
                achieved: realisees,
                awaitingRealization: aRealiser,
            });
        } catch (error) {
            commit(EQUIPEMENT.SET_ERROR, true);
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        } finally {
            commit(EQUIPEMENT.SET_PREVENTIVE_STATS_CHART_LOADING, false);
        }
    },

    async [EquipementAction.GET_DEVIS_STATS]({ commit, state }, id: number): Promise<void> {
        if (state.devisStats && state.detailsEquipement.phaseId === id && state.devisStats.nbPublies !== 0) {
            return;
        }
        commit(EQUIPEMENT.SET_DEVIS_STATS_LOADING, true);
        commit(EQUIPEMENT.SET_ERROR, false);
        try {
            const response = await EquipementStatsService.getDevisStatsByPhase(id);
            commit(EQUIPEMENT.SET_DEVIS_STATS, response.data);
        } catch (error) {
            commit(EQUIPEMENT.SET_ERROR, true);
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        } finally {
            commit(EQUIPEMENT.SET_DEVIS_STATS_LOADING, false);
        }
    },

    async [EquipementAction.GET_DEMANDES_INTERVENTION_STATS]({ commit, state }, id: number): Promise<void> {
        if (
            state.demandesInterventionStats &&
            state.detailsEquipement.phaseId === id &&
            state.demandesInterventionStats.nbPlanifie !== 0
        ) {
            return;
        }
        commit(EQUIPEMENT.SET_DEMANDES_INTERVENTION_STATS_LOADING, true);
        commit(EQUIPEMENT.SET_ERROR, false);
        try {
            const response = await EquipementStatsService.getDemandesInterventionStatsByPhase(id);
            commit(EQUIPEMENT.SET_DEMANDES_INTERVENTION_STATS, response.data);
        } catch (error) {
            commit(EQUIPEMENT.SET_ERROR, true);
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        } finally {
            commit(EQUIPEMENT.SET_DEMANDES_INTERVENTION_STATS_LOADING, false);
        }
    },

    async [EquipementAction.GET_DETAILS_EQUIPEMENT]({ commit }, id: number): Promise<void> {
        commit(EQUIPEMENT.SET_LOADING, true);
        commit(EQUIPEMENT.SET_ERROR, false);

        try {
            const response = await EquipementService.getDetailsEquipement(id);
            commit(EQUIPEMENT.SET_DETAILS_EQUIPEMENT, response.data);
        } catch (error) {
            commit(EQUIPEMENT.SET_ERROR, true);

            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });

            throw error;
        } finally {
            commit(EQUIPEMENT.SET_LOADING, false);
        }
    },

    async [EquipementAction.GET_CARACTERISTIQUES_EQUIPEMENT]({ commit }, phaseId: number) {
        commit(EQUIPEMENT.SET_IS_CARACTERISTIQUES_LOADING, true);
        try {
            const caracteristiquesResponse = await EquipementService.getCaracteristiquesEquipements(phaseId);
            commit(EQUIPEMENT.SET_CARACTERISTIQUES_EQUIPEMENT, caracteristiquesResponse.data);
        } catch (error) {
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        } finally {
            commit(EQUIPEMENT.SET_IS_CARACTERISTIQUES_LOADING, false);
        }
    },

    async [EquipementAction.GET_OPERATION_MAINTENANCE]({ commit }, phaseId: number) {
        commit(EQUIPEMENT.SET_IS_OPERATION_MAINTENANCE_LOADING, true);
        try {
            const operationssResponse = await EquipementService.getOperationsMaintenance(phaseId);
            commit(EQUIPEMENT.SET_OPERATION_MAINTENANCE, operationssResponse.data);
        } catch (error) {
            commit(`errorToaster/${ERROR.SET_MESSAGE}`, await ErrorManager.processError(error), { root: true });
        } finally {
            commit(EQUIPEMENT.SET_IS_OPERATION_MAINTENANCE_LOADING, false);
        }
    },

    [EquipementAction.HISTORIQUE_TRAVAUX_NAV_FILTER]({ commit }, filter: string[]) {
        commit(EQUIPEMENT.SET_ACTIVE_TAB, 'historiqueTravaux');
        commit(EQUIPEMENT.HISTORIQUE_TRAVAUX_SET_FILTER_STATUS, filter);
    },

    [EquipementAction.INTERVENTIONS_PREVENTIVES_NAV_FILTER]({ commit }, filter: string[]) {
        commit(EQUIPEMENT.SET_ACTIVE_TAB, 'interventionsPreventives');
        commit(EQUIPEMENT.INTERVENTIONS_PREVENTIVES_SET_FILTER_STATUS, filter);
    },

    [EquipementAction.DEVIS_NAV_FILTER]({ commit }, filter: string[]) {
        commit(EQUIPEMENT.SET_ACTIVE_TAB, 'historiqueDevis');
        commit(EQUIPEMENT.DEVIS_SET_FILTER_STATUS, filter);
    },

    [EquipementAction.DEMANDES_INTERVENTION_NAV_FILTER]({ commit }, filter: NavFilter) {
        commit(EQUIPEMENT.SET_ACTIVE_TAB, 'historiqueDemandeInterventions');
        if (filter.status) {
            commit(EQUIPEMENT.HISTORIQUE_DEMANDE_INTERVENTION_SET_FILTER_STATUS, filter.status);
        }
    },
};
